import {
  HttpClient,
  HttpClientError,
  HttpClientRequest,
} from "@effect/platform";
import {
  TeamNotificationJson,
  type TeamNotification as TeamNotificationType,
} from "@redotech/effect-api-shared/models/team/notification";
import { Effect, Option, pipe, Schema, Stream } from "effect";

/**
 * Create an Effect stream of TeamNotifications from an SSE endpoint.
 *
 * This stream:
 * 1. Establishes an HTTP connection to the SSE endpoint
 * 2. Reads the response body as an Effect stream
 * 3. Splits on SSE event boundaries ("\n\n")
 * 4. Parses and decodes each chunk into a typed TeamNotification
 *
 * Requires: HttpClient (provide FetchHttpClient.layer when running)
 */
export const createNotificationStream = (
  url: string,
  token: string,
): Stream.Stream<
  TeamNotificationType,
  HttpClientError.HttpClientError,
  HttpClient.HttpClient
> =>
  pipe(
    HttpClientRequest.get(url),
    HttpClientRequest.setHeaders({
      Authorization: `Bearer ${token}`,
      Accept: "text/event-stream",
    }),
    HttpClient.execute,
    Effect.map((response) => response.stream),
    Stream.unwrap,
    Stream.decodeText(),
    // Accumulate text and split on complete "\n\n" boundaries
    Stream.mapAccum("", (buffer, chunk) => {
      const combined = buffer + chunk; // Concatenate with buffered text
      const events = combined.split("\n\n"); // String.split() on the delimiter
      const remaining = events.pop() ?? ""; // Keep the last incomplete event in the buffer
      return [remaining, events] as const;
    }),
    // Flatten the array of events into the stream
    Stream.flatMap((events) => Stream.fromIterable(events)),
    // Filter empty strings
    Stream.filter((chunk) => chunk.trim().length > 0),
    Stream.filterMap(parseSSEChunk),
  );

/**
 * Extract and decode the data payload from an SSE chunk.
 *
 * SSE chunk format (after splitting on "\n\n"):
 * ```
 * event: messageCreated
 * data: {"_tag":"messageCreated","conversationId":"...","message":{...}}
 * ```
 *
 * Extracts everything after "data: " until the next newline, then decodes it.
 * Logs and returns None for invalid formats or decode errors (non-fatal).
 */
const parseSSEChunk = (chunk: string): Option.Option<TeamNotificationType> => {
  const dataPrefix = "data: ";
  const dataLineStart = chunk.indexOf(dataPrefix);
  if (dataLineStart === -1) {
    console.error({
      type: "SSEDecodeError",
      reason: "missing_data_field",
      chunk: chunk.slice(0, 100),
    });
    return Option.none();
  }
  const dataStart = dataLineStart + dataPrefix.length;
  const dataEnd = chunk.indexOf("\n", dataStart);
  const data =
    dataEnd === -1 ? chunk.slice(dataStart) : chunk.slice(dataStart, dataEnd);

  try {
    return Option.some(
      Schema.decodeUnknownSync(TeamNotificationJson)(JSON.parse(data), {
        onExcessProperty: "ignore",
      }),
    );
  } catch (e) {
    console.error({
      type: "SSEDecodeError",
      reason: "decode_failed",
      error: String(e),
      data: data.slice(0, 100),
    });
    return Option.none();
  }
};
